import React, {forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'
import {IconComponent, OptionWithData} from '../../types'
import {Button} from '../Button'
import {Dialog} from '../Dialog'
import deleteIcon from '../../assets/icons/delete.png'
import {useTranslation} from "react-i18next";
import {getBreakpoint} from "../../utils/getBreakpoint.ts";
import {Input} from "../Input.tsx";
import {getAgencyAssetUrl} from "../../utils/AssetsCDN.ts";
import {AutocompleteSearch} from "../AutocompleteSearch.tsx";
import {debounceTime, filter, Subject} from "rxjs";
import {useSelector} from "react-redux";
import {RootState} from "../../store.ts";
import {useTripbooks} from "../../hooks/useTripbooks.ts";
import {useAgencies} from "../../hooks/useAgencies.ts";
import {getFlagUrl} from "../../utils/flagUrl.ts";
import {CountryPickerDialog} from "./CountryPickerDialog.tsx";
import IconSpinner from "../../assets/icons/spinner.svg?react";
import moment from 'moment'
import {AnalyticsEvents, trackAnalyticsEvent} from "../../utils/ProductAnalytics.ts";
import {ref as storageRef, uploadBytesResumable, getDownloadURL} from "firebase/storage";
import {storage} from "../../firebase.ts";
import {GetItinerariesUseCase} from "../../domain/useCases/itineraries/GetItinerariesUseCase.ts";
import {ItineraryRepository} from "../../repositories/ItineraryRepository.ts";
import {AuthenticationRepository} from "../../repositories/AuthenticationRepository.ts";

type ConfirmDialogProps = {
    title?: string
    message?: string
    confirmLabel?: string
    confirmIcon?: IconComponent
    cancelLabel?: string
    children?: React.ReactNode
    onComplete: any
    tripbook: any
    openCreateTripItineraryDialog: any
}

// La API devuelve un tripbook (PUT) o una lista de tripbooks (POST bulk)
type CreatedTripbookResponse = { partnerOrderID?: string }

const currentBreakpoint = getBreakpoint()

const getItinerariesUseCase = new GetItinerariesUseCase(new ItineraryRepository(new AuthenticationRepository()));


export const CreateUpdateTripBookDialog = forwardRef<
    React.ElementRef<typeof Dialog<boolean>>,
    ConfirmDialogProps
>(({title, onComplete, tripbook}, ref) => {

    const dialog = useRef<React.ElementRef<typeof Dialog<boolean>>>(null)
    const countryPickerDialog = useRef<React.ElementRef<typeof CountryPickerDialog>>(null)

    const [itineraries, setItineraries] = useState<ItineraryRepository>()
    const { agencies } = useAgencies()
    const {t} = useTranslation()
    const user = useSelector((state: RootState) => state.auth.user)
    // 51trips staff (tourOperator y admin) eligen la agencia; un usuario agency siempre crea para la suya
    const canSelectAgency = user?.role !== 'agency'

    const { createTripbook, updateTripbook, isCreatingTripbook, isUpdatingTripbook } = useTripbooks()
    const isLoading = isCreatingTripbook || isUpdatingTripbook

    // ── Traveler ────────────────────────────────────────────────────────────
    const [travelerFirstName, setTravelerFirstName] = useState<string>('')
    const [travelerLastName, setTravelerLastName] = useState<string>('')
    const [travelerEmail, setTravelerEmail] = useState<string>('')

    // ── Trip data ───────────────────────────────────────────────────────────
    const [tripStartDate, setTripStartDate] = useState<string>('')
    const [tripEndDate, setTripEndDate] = useState<string>('')
    const [tripItinerary, setTripItinerary] = useState<any>(null)
    const [circuitName, setCircuitName] = useState<string>('')
    const [circuitCountries, setCircuitCountries] = useState<string[]>([])
    const [itinerarySearchResults, setItinerarySearchResults] = useState<OptionWithData<any>[]>([])
    const [itinerarySearchQuery, setItinerarySearchQuery] = useState<string>('')
    const [dateError, setDateError] = useState<string | undefined>(undefined)


    // ── Order / Booking ─────────────────────────────────────────────────────
    const [tripAgency, setTripAgency] = useState<any>(null)
    const [tripReference, setTripReference] = useState<string>('')
    const [tripObservations, setTripObservations] = useState<string>('')
    const [tripbooksQuantity, setTripbooksQuantity] = useState<number>(1)
    const [tripbookId, setTripbookId] = useState<string>('')
    const [agencySearchResults, setAgencySearchResults] = useState<OptionWithData<any>[]>([])
    const [agencySearchQuery, setAgencySearchQuery] = useState<string>('')

    // ── PDF Itinerary ────────────────────────────────────────────────────────
    const [itineraryPdfFileUrl, setItineraryPdfFileUrl] = useState<string>('')
    const [pdfFileName, setPdfFileName] = useState<string>('')
    const [isUploadingPdf, setIsUploadingPdf] = useState<boolean>(false)
    const pdfInputRef = useRef<HTMLInputElement>(null)

    // ── Subjects ────────────────────────────────────────────────────────────
    const inputItinerarySubject = useMemo<Subject<string>>(() => new Subject<string>(), [])
    const inputAgencySubject = useMemo<Subject<string>>(() => new Subject<string>(), [])

    useEffect(() => {
        getItinerariesUseCase.execute().then(
            (response) => {
                console.log(response)
                setItineraries(response)
            }
        )
    }, []);

    // ── Effects ─────────────────────────────────────────────────────────────
    useEffect(() => {
        if (itineraries !== undefined) {
            const subscription = inputItinerarySubject
                .pipe(debounceTime(700), filter((q) => q !== ''))
                .subscribe({
                    next: (q) => {
                        const norm = q.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase()
                        setItinerarySearchResults(
                            itineraries
                                .filter((i: any) => i.name.toLowerCase().includes(norm))
                                .map((i: any) => ({value: i.name, label: i.name, data: i}))
                        )
                    },
                    error: console.error
                })
            return () => subscription.unsubscribe()
        }
    }, [itineraries])

    useEffect(() => {
        if (agencies !== undefined) {
            const subscription = inputAgencySubject
                .pipe(debounceTime(700), filter((q) => q !== ''))
                .subscribe({
                    next: (q) => {
                        const norm = q.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase()
                        setAgencySearchResults(
                            agencies
                                .filter((a: any) => a.nameLowerCase?.includes(norm))
                                .map((a: any) => ({value: a.name, label: a.name, data: a}))
                        )
                    },
                    error: console.error
                })
            return () => subscription.unsubscribe()
        }
    }, [agencies])

    useEffect(() => {
        if (tripbook?.itinerary?.startDate) {
            setTripStartDate(moment(tripbook.itinerary.startDate).format('YYYY-MM-DD'))
        }
        if (tripbook?.itinerary?.endDate) {
            setTripEndDate(moment(tripbook.itinerary.endDate).format('YYYY-MM-DD'))
        }
        setTripAgency(tripbook?.agencyData ?? null)
        setTripReference(tripbook?.partnerOrderID ?? '')
        setTripItinerary(tripbook?.itinerary ?? null)
        setCircuitName(tripbook?.itinerary?.name ?? '')
        setCircuitCountries(tripbook?.itinerary?.countryCodes ?? [])
        setTripObservations(tripbook?.observations ?? '')
        setTripbookId(tripbook?.id ?? '')
        setItineraryPdfFileUrl(tripbook?.itineraryPdfFileUrl ?? '')
        setPdfFileName(tripbook?.itineraryPdfFileUrl ? tripbook.itineraryPdfFileUrl.split('/').pop()?.split('?')[0] ?? '' : '')
    }, [tripbook])

    // ── Imperativehandle ────────────────────────────────────────────────────
    useImperativeHandle(ref, () => ({
        open: () => dialog.current!.open(),
        close: dialog.current!.close
    }), [])

    // ── Handlers ────────────────────────────────────────────────────────────
    const handleDateChange = (start: string, end: string) => {
        if (start && end && start > end) {
            setDateError(t('invalidStartDate', {ns: 'dashboard'}))
        } else {
            setDateError(undefined)
        }
    }

    const selectItinerary = (itinerary: any) => {
        setTripItinerary(itinerary)
        setCircuitName(itinerary.name ?? '')
        setCircuitCountries(itinerary.countryCodes ?? [])
        setItinerarySearchQuery('')
        setItinerarySearchResults([])
    }

    const clearItinerary = () => {
        setTripItinerary(null)
        setCircuitName('')
        setCircuitCountries([])
    }

    const registerAgencyName = () => {
        const name = agencySearchQuery.trim()
        if (!name) return
        setTripAgency({name})
        setAgencySearchQuery('')
        setAgencySearchResults([])
    }

    const cleanForm = () => {
        setTravelerFirstName('')
        setTravelerLastName('')
        setTravelerEmail('')
        setTripStartDate('')
        setTripEndDate('')
        setTripItinerary(null)
        setCircuitName('')
        setCircuitCountries([])
        setItinerarySearchResults([])
        setItinerarySearchQuery('')

        setTripAgency(null)
        setTripReference('')
        setTripObservations('')
        setTripbooksQuantity(1)
        setTripbookId('')
        setAgencySearchResults([])
        setAgencySearchQuery('')
        setDateError(undefined)
        setItineraryPdfFileUrl('')
        setPdfFileName('')
        setIsUploadingPdf(false)
        if (pdfInputRef.current) pdfInputRef.current.value = ''
    }

    const handlePdfFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        setIsUploadingPdf(true)
        setPdfFileName(file.name)
        const path = `tripItineraries/${Date.now()}_${file.name}`
        const fileRef = storageRef(storage, path)
        const uploadTask = uploadBytesResumable(fileRef, file)
        uploadTask.on('state_changed', null,
            (error) => { console.error(error); setIsUploadingPdf(false) },
            async () => {
                const url = await getDownloadURL(uploadTask.snapshot.ref)
                setItineraryPdfFileUrl(url)
                setIsUploadingPdf(false)
            }
        )
    }

    const buildItinerary = () => {
        if (tripItinerary) {
            return {
                name: tripItinerary.name ||  t('itineraryTitlePlaceholder', {ns: 'dashboard'}),
                tripImageUrl: tripItinerary.tripImageUrl,
                countryCodes: tripItinerary.countryCodes,
                startDate: tripStartDate ? moment.utc(tripStartDate, 'YYYY-MM-DD').valueOf() : undefined,
                endDate: tripEndDate ? moment.utc(tripEndDate, 'YYYY-MM-DD').valueOf() : undefined,
            }
        }
        console.log('circuitCountries', circuitCountries)
        if (circuitName || circuitCountries.length > 0) {
            return {
                name: circuitName || t('itineraryTitlePlaceholder', {ns: 'dashboard'}),
                countryCodes: circuitCountries.length > 0 ? circuitCountries : undefined,
                personalized: true,
                startDate: tripStartDate ? moment.utc(tripStartDate, 'YYYY-MM-DD').valueOf() : undefined,
                endDate: tripEndDate ? moment.utc(tripEndDate, 'YYYY-MM-DD').valueOf() : undefined,
            }
        }
        return undefined
    }

    const buildPayload = () => {
        const agencyFields = user.role === "agency"
            ? {agencyID: user.id}
            : tripAgency?.id
                ? {agencyID: tripAgency.id}
                : tripAgency?.name
                    ? {agencyName: tripAgency.name}
                    : {}

        return {
            partnerOrderID: tripReference || undefined,
            ...agencyFields,

            itinerary: buildItinerary(),
            observations: tripObservations?.length > 0 ? tripObservations : undefined,
            quantity: tripbooksQuantity,
            traveler: travelerFirstName || travelerLastName || travelerEmail ? {
                firstName: travelerFirstName || undefined,
                lastName: travelerLastName || undefined,
                email: travelerEmail || undefined,
            } : undefined,
            itineraryPdfFileUrl: itineraryPdfFileUrl || undefined,
        }
    }

    const submit = () => {
        const isCreating = !tripbookId

        trackAnalyticsEvent(isCreating ? AnalyticsEvents.CREATE_TRIPBOOK : AnalyticsEvents.UPDATE_TRIPBOOK, {
            customItinerary: !tripItinerary && (!!circuitName || circuitCountries.length > 0),
        })

        const payload = buildPayload()
        const action = isCreating
            ? createTripbook(payload as any)
            : updateTripbook({id: tripbookId, ...payload} as any)

        action
            .then((response) => {
                // en creaciones bulk (quantity > 1) la respuesta es una lista de tripbooks
                const created = (Array.isArray(response) ? response[0] : response) as CreatedTripbookResponse
                onComplete({
                    ...created,
                    agencyData: tripAgency,
                    // usamos la referencia del formulario: la de la respuesta llega con
                    // el sufijo de cantidad (ref-1, ref-2...) en creaciones bulk
                    partnerOrderID: payload.partnerOrderID ?? created?.partnerOrderID,
                })
                cleanForm()
                dialog.current!.close()
            })
            .catch(console.error)
    }

    const isFormEnabled = () => {
        if (isLoading) return false
        if (isUploadingPdf) return false
        if (!tripReference) return false
        if (canSelectAgency && !tripAgency) return false
        if (dateError) return false
        return true
    }

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <Dialog
            ref={dialog}
            title={title}
            customWidth={currentBreakpoint.isMobile ? undefined : '80vw'}
            width={currentBreakpoint.isMobile ? 'full' : undefined}
            height={currentBreakpoint.isMobile ? 'full' : 'auto'}
            withHeaderDelimiter
            headerButtons={[
                <Button key="complete" variant="primary" size="small"
                        disabled={!isFormEnabled()} onClick={submit} className="max-h-[32px] capitalize">
                    {!tripbookId ? t('create', {ns: 'dashboard'}) : t('edit', {ns: 'dashboard'})}
                </Button>,
                <Button key="close" variant="secondary" size="small"
                        onClick={() => { cleanForm(); dialog.current!.close() }} className="max-h-[32px]">
                    {t('cancel', {ns: 'dashboard'})}
                </Button>,
            ]}
        >
            <div id="createTripbookForm" className="flex flex-col gap-6 overflow-y-auto no-scrollbar">

                {/* ── Layout: 3 columnas en desktop ────────────────────────── */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                    {/* ── Columna 1: Pedido / Reserva ──────────────────────── */}
                    <section className="flex flex-col gap-4">
                        <h3 className="text-[13px] font-semibold text-neutralBlack-500 uppercase tracking-wide">
                            {t('orderData', {ns: 'dashboard'})}
                        </h3>

                        {/* Referencia — único campo obligatorio */}
                        <div className="flex flex-col gap-1">
                            <label htmlFor="tripReference" className="text-[14px] font-medium">
                                <span className="text-danger mr-0.5">*</span>
                                {t('writeTripReference', {ns: 'dashboard'})}
                            </label>
                            <Input id="tripReference" type="text" invalid={!tripReference && tripReference !== ''}
                                   placeholder="KJLIT65T" value={tripReference}
                                   onChange={(e) => setTripReference(e.target.value)}
                                   className="h-[40px]"/>
                        </div>

                        {/* Agencia (staff de 51trips: tourOperator y admin) */}
                        {canSelectAgency && (
                            <div className="flex flex-col gap-1">
                                <label className="text-[14px] font-medium">
                                    <span className="text-danger mr-0.5">*</span>
                                    {t('selectAgency', {ns: 'dashboard'})}
                                </label>
                                {!tripAgency ? (
                                    <>
                                        <AutocompleteSearch
                                            options={agencySearchResults}
                                            placeholder={t('searchYourAgency', {ns: 'dashboard'})}
                                            searchQuery={agencySearchQuery}
                                            onSearchQueryChange={(v) => { setAgencySearchQuery(v); inputAgencySubject.next(v) }}
                                            onSelectOption={(opt) => { setTripAgency(opt.data); setAgencySearchQuery('') }}
                                        />
                                        {agencySearchQuery.trim() && agencySearchResults.length === 0 && (
                                            <div className="flex flex-row gap-1 text-[13px] mt-1">
                                                <span className="text-neutralBlack-500">{t('noFindAgency', {ns: 'dashboard'})}</span>
                                                <button className="font-medium cursor-pointer" onClick={registerAgencyName}>
                                                    {t('addNewAgencyFromTripbook', {ns: 'dashboard'})}
                                                </button>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="bg-neutralBlack-100 flex flex-row items-center gap-3 w-full h-[52px] rounded-[41px] px-4 justify-between">
                                        <div className="flex flex-row items-center gap-2 min-w-0">
                                            {tripAgency.logoName && (
                                                <div className="w-[32px] h-[32px] bg-white rounded-full p-1 flex items-center shrink-0">
                                                    <img src={getAgencyAssetUrl(tripAgency.logoName)} alt={tripAgency.name} className="w-full h-full object-contain"/>
                                                </div>
                                            )}
                                            <div className="min-w-0">
                                                <div className="text-[14px] font-medium capitalize truncate">{tripAgency.name}</div>
                                                {!tripAgency.id && (
                                                    <div className="text-[11px] text-neutralBlack-500">{t('newAgencyLabel', {ns: 'dashboard'})}</div>
                                                )}
                                            </div>
                                        </div>
                                        <img src={deleteIcon} alt="remove" className="size-[15px] cursor-pointer shrink-0"
                                             onClick={() => setTripAgency(null)}/>
                                    </div>
                                )}
                            </div>
                        )}

                            <div className="flex flex-col gap-1">
                                <label htmlFor="quantity" className="text-[14px] font-medium">{t('quantity', {ns: 'dashboard'})}</label>
                                <Input id="quantity" type="number" invalid={false}
                                       value={tripbooksQuantity}
                                       onChange={(e) => setTripbooksQuantity(Number(e.target.value))}
                                       className="h-[40px]"/>
                            </div>


                        {/* Observaciones */}
                        <div className="flex flex-col gap-1">
                            <label htmlFor="observations" className="text-[14px] font-medium">{t('observations', {ns: 'dashboard'})}</label>
                            <Input id="observations" type="text" invalid={false}
                                   placeholder={t('addAdditionalInformation', {ns: 'dashboard'})}
                                   value={tripObservations}
                                   onChange={(e) => setTripObservations(e.target.value)}
                                   className="h-[40px]"/>
                        </div>
                    </section>

                    {/* ── Columna 2: Datos del viaje ───────────────────────── */}
                    <section className="flex flex-col gap-4">
                        <h3 className="text-[13px] font-semibold text-neutralBlack-500 uppercase tracking-wide">
                            {t('tripData', {ns: 'dashboard'})}
                        </h3>

                        {/* Fechas */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[14px] font-medium">{t('selectDates', {ns: 'dashboard'})}</label>
                            <div className="flex flex-col gap-2">
                                <div className="flex flex-col gap-1">
                                    <span className="text-[12px] text-neutralBlack-500">{t('start', {ns: 'dashboard'})}</span>
                                    <input
                                        type="date"
                                        value={tripStartDate}
                                        onChange={(e) => {
                                            setTripStartDate(e.target.value)
                                            handleDateChange(e.target.value, tripEndDate)
                                        }}
                                        className="h-[40px] border border-neutralBlack-300 rounded-[4px] px-3 text-[14px] bg-white focus:outline-none focus:border-primary"
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <span className="text-[12px] text-neutralBlack-500">{t('end', {ns: 'dashboard'})}</span>
                                    <input
                                        type="date"
                                        value={tripEndDate}
                                        min={tripStartDate || undefined}
                                        onChange={(e) => {
                                            setTripEndDate(e.target.value)
                                            handleDateChange(tripStartDate, e.target.value)
                                        }}
                                        className="h-[40px] border border-neutralBlack-300 rounded-[4px] px-3 text-[14px] bg-white focus:outline-none focus:border-primary"
                                    />
                                </div>
                            </div>
                            {dateError && <div className="text-[13px] text-danger">{dateError}</div>}
                        </div>

                        {/* Destinos */}
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center justify-between">
                                <label className="text-[14px] font-medium">
                                    {t('countryCountries', {ns: 'dashboard'})}
                                </label>
                                <button
                                    className="text-[13px] font-medium text-danger cursor-pointer"
                                    onClick={() => countryPickerDialog.current!.open()}
                                >
                                    {t(circuitCountries.length > 0 ? 'edit' : 'add', {ns: 'dashboard'})}
                                </button>
                            </div>
                            {circuitCountries.length > 0 && (
                                <div className="flex flex-row gap-2 flex-wrap mt-1">
                                    {circuitCountries.map((code) => (
                                        <img key={code} src={getFlagUrl(code)} alt={code}
                                             className="h-[22px] rounded-sm"
                                             onError={(e) => {
                                                 e.currentTarget.src = 'https://storage.googleapis.com/tripsapp-prod-cdn/backgroundCoverNoImage.jpg'
                                             }}/>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* PDF del itinerario */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[14px] font-medium">{t('itineraryPdf', {ns: 'dashboard'})}</label>
                            <input
                                ref={pdfInputRef}
                                type="file"
                                accept=".pdf,application/pdf"
                                className="hidden"
                                onChange={handlePdfFileSelected}
                            />
                            {!itineraryPdfFileUrl && !isUploadingPdf && (
                                <button
                                    type="button"
                                    onClick={() => pdfInputRef.current?.click()}
                                    className="h-[40px] flex items-center gap-2 border border-dashed border-neutralBlack-300 rounded-[4px] px-3 text-[14px] text-neutralBlack-500 hover:border-neutralBlack-500 transition-colors duration-200 ease-out cursor-pointer"
                                >
                                    <span className="text-[16px]">📎</span>
                                    {t('itineraryPdfAttach', {ns: 'dashboard'})}
                                </button>
                            )}
                            {isUploadingPdf && (
                                <div className="h-[40px] flex items-center gap-2 border border-neutralBlack-200 rounded-[4px] px-3 text-[14px] text-neutralBlack-500">
                                    <IconSpinner className="size-4 shrink-0"/>
                                    <span className="truncate">{t('uploadingPdf', {ns: 'dashboard'})}</span>
                                </div>
                            )}
                            {itineraryPdfFileUrl && !isUploadingPdf && (
                                <div className="h-[40px] flex items-center gap-2 border border-neutralBlack-200 rounded-[4px] px-3 text-[14px]">
                                    <span className="text-[16px] shrink-0">📄</span>
                                    <span className="flex-1 truncate text-neutralBlack-700">{pdfFileName}</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setItineraryPdfFileUrl('')
                                            setPdfFileName('')
                                            if (pdfInputRef.current) pdfInputRef.current.value = ''
                                        }}
                                        className="text-danger text-[13px] font-medium shrink-0 cursor-pointer"
                                    >
                                        {t('remove', {ns: 'dashboard'})}
                                    </button>
                                </div>
                            )}
                        </div>

                    </section>

                    {/* ── Columna 3: Itinerario + Viajero ─────────────────── */}
                    <section className="flex flex-col gap-4">
                        <h3 className="text-[13px] font-semibold text-neutralBlack-500 uppercase tracking-wide">
                            {t('selectItinerary', {ns: 'dashboard'})}
                        </h3>

                        {/* Búsqueda de itinerario */}
                        <div className="flex flex-col gap-1">
                            {!tripItinerary ? (
                                <AutocompleteSearch
                                    options={itinerarySearchResults}
                                    placeholder={t('searchYourItinerary', {ns: 'dashboard'})}
                                    searchQuery={itinerarySearchQuery}
                                    onSearchQueryChange={(v) => { setItinerarySearchQuery(v); inputItinerarySubject.next(v) }}
                                    onSelectOption={(opt) => selectItinerary(opt.data)}
                                />
                            ) : (
                                <div className="bg-neutralBlack-100 flex flex-row items-center gap-3 w-full h-[60px] rounded-md p-2">
                                    <div className="h-[44px] w-[72px] shrink-0 rounded overflow-hidden">
                                        <img className="h-full w-full object-cover" src={tripItinerary.tripImageUrl} alt={tripItinerary.name}/>
                                    </div>
                                    <div className="flex flex-col flex-1 min-w-0">
                                        <div className="text-[14px] font-medium truncate capitalize">{tripItinerary.name}</div>
                                        <div className="flex flex-row gap-1 mt-0.5">
                                            {tripItinerary.countryCodes?.map((code: string) => (
                                                <img key={code} src={getFlagUrl(code)} alt={code}
                                                     className="h-[14px]"
                                                     onError={(e) => {
                                                         e.currentTarget.src = 'https://storage.googleapis.com/tripsapp-prod-cdn/backgroundCoverNoImage.jpg'
                                                     }}/>
                                            ))}
                                        </div>
                                    </div>
                                    <img src={deleteIcon} alt="remove" className="size-[15px] cursor-pointer shrink-0"
                                         onClick={clearItinerary}/>
                                </div>
                            )}
                            <div className="flex flex-row gap-1 text-[13px] mt-1">
                                <span className="text-neutralBlack-500">{t('noFindItinerary', {ns: 'dashboard'})}</span>
                                <button className="font-medium cursor-pointer" onClick={() => setTripItinerary(null)}>
                                    {t('createPersonalizedItinerary', {ns: 'dashboard'})}
                                </button>
                            </div>
                        </div>

                        {/* Nombre del circuito */}
                        <div className="flex flex-col gap-1">
                            <label htmlFor="circuitName" className="text-[14px] font-medium">{t('circuitName', {ns: 'dashboard'})}</label>
                            <Input id="circuitName" type="text" invalid={false}
                                   placeholder={t('itineraryTitlePlaceholder', {ns: 'dashboard'})}
                                   value={circuitName}
                                   onChange={(e) => setCircuitName(e.target.value)}
                                   className="h-[40px]"/>
                        </div>

                        <div className="border-t border-neutralBlack-200"/>

                        {/* Viajero principal */}
                        <h3 className="text-[13px] font-semibold text-neutralBlack-500 uppercase tracking-wide">
                            {t('mainTraveler', {ns: 'dashboard'})}
                        </h3>
                        <div className="flex flex-col gap-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="travelerFirstName" className="text-[14px] font-medium">{t('firstName', {ns: 'dashboard'})}</label>
                                    <Input id="travelerFirstName" type="text" invalid={false}
                                           placeholder={t('dialogs.placeholderFirstName', {ns: 'dashboard'})} value={travelerFirstName}
                                           onChange={(e) => setTravelerFirstName(e.target.value)}
                                           className="h-[40px]"/>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="travelerLastName" className="text-[14px] font-medium">{t('lastName', {ns: 'dashboard'})}</label>
                                    <Input id="travelerLastName" type="text" invalid={false}
                                           placeholder={t('dialogs.placeholderLastName', {ns: 'dashboard'})} value={travelerLastName}
                                           onChange={(e) => setTravelerLastName(e.target.value)}
                                           className="h-[40px]"/>
                                </div>
                            </div>
                            <div className="flex flex-col gap-1">
                                <label htmlFor="travelerEmail" className="text-[14px] font-medium">{t('email', {ns: 'dashboard'})}</label>
                                <Input id="travelerEmail" type="email" invalid={false}
                                       placeholder={t('dialogs.placeholderTravelerEmail', {ns: 'dashboard'})} value={travelerEmail}
                                       onChange={(e) => setTravelerEmail(e.target.value)}
                                       className="h-[40px]"/>
                            </div>
                        </div>
                    </section>

                </div>

            </div>

            <CountryPickerDialog
                ref={countryPickerDialog}
                selectedCountriesIDs={circuitCountries || []}
                onSelectedCountriesIDsChanged={setCircuitCountries}
            />

            {isLoading && (
                <div className="absolute inset-0 bg-neutralBlack-100 opacity-75 flex rounded-xl">
                    <IconSpinner className="size-16 m-auto"/>
                </div>
            )}
        </Dialog>
    )
})
